/**
 * The meter register: every electricity, water, gas and chilled-water meter
 * at the site, what it measures, its last reading and how old that is, and
 * whether it is reporting — live, stale, or never.
 *
 * Three ways a meter's readings reach the platform, and this screen is where
 * each is set up: a BMS that can post is given the push path; the on-site
 * gateway (backend/scripts/meter_gateway.py) posts to the same place; and a
 * BMS that can only export is uploaded here, as a file shaped by the template
 * this screen hands out with the site's own meters filled in. All three go
 * through the same connector, so a file uploaded twice, or a reading the
 * gateway sends again, is recognised and changes nothing.
 *
 * The Green programme reads these meters. Until one reports, it shows its
 * demonstration series and says so.
 */
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { useMeters, useRegisterMeter, useUploadMeterReadings } from "../../api/queries";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Segmented,
  Select,
  Skeleton,
  Table,
} from "../../design/components";
import {
  csvTemplate,
  METER_ASSET_TYPES,
  ROLE_LABEL,
  STATUS_LABEL,
  UTILITIES,
  utilityOf,
  type MeterRow,
  type MeterUploadResult,
  type NewMeterInput,
  type ReadingKind,
  type Utility,
} from "../../domain/meters";
import { locale, t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { saveFile } from "../../lib/saveFile";
import { Dialog } from "../stores/Purchasing";
import s from "./Meters.module.css";

type Filter = "all" | Utility;

/** A reading's age in words a person reads at a glance. */
function age(minutes: number | undefined): string {
  if (minutes == null) return "—";
  if (minutes < 1) return t("just now");
  if (minutes < 90) return t("{n} min", { n: Math.round(minutes) });
  if (minutes < 48 * 60) return t("{n} h", { n: Math.round(minutes / 60) });
  return t("{n} d", { n: Math.round(minutes / 1440) });
}

function value(m: MeterRow): string {
  if (m.lastValue == null) return "—";
  return `${m.lastValue.toLocaleString(locale(), { maximumFractionDigits: 2 })} ${m.unit ?? ""}`.trim();
}

export function MetersPage() {
  const session = useSession();
  const reg = useMeters(METER_ASSET_TYPES);
  const [filter, setFilter] = useState<Filter>("all");
  const [connect, setConnect] = useState<NewMeterInput | null>(null);
  const meters = useMemo(() => reg.data?.meters ?? [], [reg.data]);
  const shown = meters.filter((m) => filter === "all" || utilityOf(m.assetType)?.key === filter);
  const count = (st: MeterRow["status"]) => meters.filter((m) => m.status === st).length;
  const live = count("live");
  const stale = count("stale");
  const never = count("never");
  const connected = meters.filter((m) => m.channelId).length;

  useScreenContext({
    route: "/meters",
    title: "Meter register",
    summary: [
      t("{n} meters: {live} live, {stale} stale, {never} never reported.", {
        n: meters.length,
        live,
        stale,
        never,
      }),
      t("{c} of {n} have a feed connected.", { c: connected, n: meters.length }),
    ],
    offers: [{ label: t("Which meters are not reporting?"), ask: "which meters are not reporting?" }],
    worksInFull: true,
  });

  return (
    <Page eyebrow={t("{site} · Aerodrome", { site: session.site.code })} title="Meter register">
      <p className={s.lede}>
        {t(
          "Every electricity, water, gas, and chilled-water meter, its last reading, and whether it reports.",
        )}{" "}
        <Link to="/green-program">{t("Green & LEED →")}</Link>
      </p>
      {reg.data?.demo && (
        <p className={s.note} data-testid="meters-sample">
          {t(
            "Sample data. These meters aren't connected, so the Green program shows demo figures.",
          )}
        </p>
      )}
      <div className={s.bar}>
        <Segmented
          ariaLabel="Utility"
          value={filter}
          onChange={setFilter}
          options={[{ value: "all", label: "All" }, ...UTILITIES.map((u) => ({ value: u.key, label: u.label }))]}
        />
        <span className={s.count} data-testid="meters-count">
          {t("{live} live · {stale} stale · {never} never reported · {c} of {n} connected", {
            live,
            stale,
            never,
            c: connected,
            n: meters.length,
          })}
        </span>
        <Button
          variant="primary"
          onClick={() =>
            setConnect({
              assetTag: "",
              utility: filter === "all" ? "electricity" : filter,
              readingKind: "cumulative",
              staleAfterMin: 360,
            })
          }
        >
          {t("Connect a meter")}
        </Button>
      </div>

      {reg.isPending ? (
        <Skeleton rows={6} />
      ) : reg.isError ? (
        <ErrorState error={reg.error} retry={() => void reg.refetch()} />
      ) : shown.length === 0 ? (
        <EmptyState>{t("No meters of this kind on the register.")}</EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>{t("Meter")}</th>
              <th>{t("Measures")}</th>
              <th>{t("Feed")}</th>
              <th className={s.num}>{t("Last reading")}</th>
              <th className={s.num}>{t("Age")}</th>
              <th>{t("Status")}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((m) => {
              const u = utilityOf(m.assetType);
              return (
                <tr key={m.assetId} data-testid={`meter-${m.assetTag}`}>
                  <td>
                    <b>{m.assetTag}</b>
                    <div className={s.dim}>
                      {[m.name, m.role && t(ROLE_LABEL[m.role] ?? m.role), m.serves].filter(Boolean).join(" · ")}
                    </div>
                  </td>
                  <td>{u ? `${t(u.label)} (${m.unit ?? u.unit})` : m.assetTypeName}</td>
                  <td>
                    {m.channelId ? (
                      <>
                        <span className={s.code}>{m.pointKey}</span>
                        <div className={s.dim}>
                          {t(m.readingKind === "interval" ? "amount per interval" : "register")}
                          {m.rollovers > 0 && ` · ${t("{n} rollovers", { n: m.rollovers })}`}
                          {m.resets > 0 && ` · ${t("{n} resets", { n: m.resets })}`}
                        </div>
                      </>
                    ) : (
                      <span className={s.dim}>{t("not connected")}</span>
                    )}
                  </td>
                  <td className={s.num}>
                    {value(m)}
                    {m.lastSource && <div className={s.dim}>{m.lastSource}</div>}
                  </td>
                  <td className={s.num}>{age(m.ageMin)}</td>
                  <td>
                    <span className={s.status} data-status={m.status}>
                      {t(STATUS_LABEL[m.status])}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <div className={s.two}>
        <Card title="How readings arrive" sub="From a BMS, the site gateway, or a file">
          {reg.data?.connector ? (
            <p className={s.result}>
              <span>{t("A BMS, or the on-site gateway, posts the site's readings to")}</span>
              <span className={s.code} data-testid="meters-push-path">
                POST {reg.data.connector.pushPath}
              </span>
              <span className={s.dim}>
                {t(
                  "using a key that can only write readings. Times without an offset use site time. See docs/CONNECTING.md, “Meters”.",
                )}
              </span>
            </p>
          ) : (
            <p className={s.dim}>
              {t(
                "No meter is connected yet. Connect one to get the feed address.",
              )}
            </p>
          )}
        </Card>
        <UploadCard meters={meters} />
      </div>

      {connect && <ConnectDialog start={connect} meters={meters} onClose={() => setConnect(null)} />}
    </Page>
  );
}

function UploadCard({ meters }: { meters: readonly MeterRow[] }) {
  const upload = useUploadMeterReadings();
  const file = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<MeterUploadResult | null>(null);
  const send = async (f: File) => {
    setResult(null);
    const csv = await f.text();
    upload.mutate({ csv, filename: f.name }, { onSuccess: setResult });
  };
  return (
    <Card title="Upload readings" sub="For a BMS that only exports files">
      <p className={s.dim}>
        {t(
          "A CSV with meter, time, and value, plus optional reading type. Comma, semicolon, or tab. Uploading the same file twice changes nothing.",
        )}
      </p>
      <div className={s.bar}>
        <Button
          onClick={() => void saveFile("meter-readings-template.csv", csvTemplate(meters), "text/csv")}
          data-testid="meters-template"
        >
          {t("Download the template")}
        </Button>
        <input
          ref={file}
          type="file"
          accept=".csv,text/csv,text/plain"
          aria-label={t("Readings file")}
          data-testid="meters-file"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void send(f);
            e.target.value = "";
          }}
        />
      </div>
      {upload.isPending && <p className={s.dim}>{t("Uploading…")}</p>}
      {upload.isError && <p className={s.bad}>{(upload.error as Error).message}</p>}
      {result && (
        <div className={s.result} data-testid="meters-upload-result">
          <span>
            {result.replayed
              ? t("This file was uploaded before; nothing new was written.")
              : t("{a} accepted · {d} already held · {r} refused, of {n}", {
                  a: result.accepted,
                  d: result.duplicate,
                  r: result.rejected,
                  n: result.received,
                })}
          </span>
          {result.rejections.slice(0, 8).map((x, i) => (
            <span key={i} className={s.bad}>
              {x.line ? t("line {line}: {reason}", { line: x.line, reason: x.reason }) : x.reason}
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

function ConnectDialog({
  start,
  meters,
  onClose,
}: {
  start: NewMeterInput;
  meters: readonly MeterRow[];
  onClose: () => void;
}) {
  const save = useRegisterMeter();
  const [v, setV] = useState<NewMeterInput>(start);
  const [isNew, setIsNew] = useState(false);
  const u = UTILITIES.find((x) => x.key === v.utility)!;
  const candidates = meters.filter((m) => m.assetType === u.assetType);
  const set = (patch: Partial<NewMeterInput>) => setV((cur) => ({ ...cur, ...patch }));
  const ok = v.assetTag.trim() !== "" && (!isNew || (v.createName ?? "").trim() !== "");
  return (
    <Dialog title={t("Connect a meter")} onClose={onClose} testId="meter-dialog">
      <div className={s.formRow}>
        <Field label="Utility">
          <Select
            value={v.utility}
            onChange={(e) => set({ utility: e.target.value as Utility, assetTag: "" })}
          >
            {UTILITIES.map((x) => (
              <option key={x.key} value={x.key}>
                {t(x.label)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Meter">
          {isNew ? (
            <Input value={v.assetTag} onChange={(e) => set({ assetTag: e.target.value })} />
          ) : (
            <Select value={v.assetTag} onChange={(e) => set({ assetTag: e.target.value })}>
              <option value="">{t("choose a meter")}</option>
              {candidates.map((m) => (
                <option key={m.assetId} value={m.assetTag}>
                  {m.assetTag}
                  {m.name ? ` — ${m.name}` : ""}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      <label className={s.dim}>
        <input type="checkbox" checked={isNew} onChange={(e) => setIsNew(e.target.checked)} />{" "}
        {t("a meter not yet on the register")}
      </label>
      {isNew && (
        <Field label="Name">
          <Input value={v.createName ?? ""} onChange={(e) => set({ createName: e.target.value })} />
        </Field>
      )}
      <div className={s.formRow}>
        <Field label="Point key on the BMS">
          <Input
            value={v.pointKey ?? ""}
            placeholder={v.assetTag || t("the meter's tag")}
            onChange={(e) => set({ pointKey: e.target.value })}
          />
        </Field>
        <Field label="The number it sends">
          <Select value={v.readingKind} onChange={(e) => set({ readingKind: e.target.value as ReadingKind })}>
            <option value="cumulative">{t("the register (a running total)")}</option>
            <option value="interval">{t("the amount used since the last reading")}</option>
          </Select>
        </Field>
      </div>
      <div className={s.formRow}>
        <Field label="Register digits">
          <Input
            type="number"
            min={1}
            max={15}
            value={v.registerDigits ?? ""}
            placeholder={t("from the asset")}
            onChange={(e) => set({ registerDigits: e.target.value ? Number(e.target.value) : undefined })}
          />
        </Field>
        <Field label="Multiplier">
          <Input
            type="number"
            step="any"
            min={0}
            value={v.scale ?? ""}
            placeholder="1"
            onChange={(e) => set({ scale: e.target.value ? Number(e.target.value) : undefined })}
          />
        </Field>
        <Field label="Stale after (minutes)">
          <Input
            type="number"
            min={5}
            value={v.staleAfterMin ?? 360}
            onChange={(e) => set({ staleAfterMin: Number(e.target.value) || 360 })}
          />
        </Field>
      </div>
      {save.isError && <p className={s.bad}>{(save.error as Error).message}</p>}
      <div className={s.formFoot}>
        <Button onClick={onClose}>{t("Cancel")}</Button>
        <Button
          variant="primary"
          disabled={!ok || save.isPending}
          data-testid="meter-save"
          onClick={() => save.mutate(isNew ? v : { ...v, createName: undefined }, { onSuccess: onClose })}
        >
          {t("Connect")}
        </Button>
      </div>
    </Dialog>
  );
}
